import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Send as SendIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-reminders-tabpanel-${index}`}
      aria-labelledby={`admin-reminders-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const RemindersAdminPage: React.FC = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [upcomingReminders, setUpcomingReminders] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [copiedPin, setCopiedPin] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsRes, upcomingRes, logsRes, auditRes, subsRes] = await Promise.all([
        api.get('/reminders/admin/dashboard'),
        api.get('/reminders/admin/upcoming?days=30'),
        api.get('/reminders/admin/logs?days=7'),
        api.get('/reminders/admin/audit?days=30'),
        api.get('/reminders/admin/subscriptions'),
      ]);

      setStats(statsRes.data);
      setUpcomingReminders(upcomingRes.data);
      setLogs(logsRes.data);
      setAuditLogs(auditRes.data);
      setSubscriptions(subsRes.data);
    } catch (err: any) {
      console.error('Error loading admin data:', err);
      setError(t('reminders.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendNotification = async (logId: string) => {
    try {
      await api.post(`/reminders/admin/notifications/resend/${logId}`);
      loadData();
    } catch (err) {
      console.error('Error resending notification:', err);
    }
  };

  const handleResendPin = async (subscriptionId: string, email: string) => {
    try {
      await api.post(`/reminders/admin/subscriptions/${subscriptionId}/resend-pin`);
      alert(`PIN resent to ${email}`);
    } catch (err) {
      console.error('Error resending PIN:', err);
      alert('Failed to resend PIN');
    }
  };

  const handleCopyPin = (pin: string) => {
    navigator.clipboard.writeText(pin);
    setCopiedPin(pin);
    setTimeout(() => setCopiedPin(null), 2000);
  };

  const getUrgencyColor = (daysUntil: number) => {
    if (daysUntil < 0) return 'error';
    if (daysUntil === 0) return 'error';
    if (daysUntil <= 7) return 'warning';
    if (daysUntil <= 30) return 'info';
    return 'default';
  };

  const getStatusChip = (status: string) => {
    const statusConfig: any = {
      sent: { color: 'success', icon: <CheckCircleIcon /> },
      failed: { color: 'error', icon: <ErrorIcon /> },
      pending: { color: 'warning', icon: <ScheduleIcon /> },
      overdue: { color: 'error', icon: <WarningIcon /> },
    };

    const config = statusConfig[status] || { color: 'default' };

    return (
      <Chip
        size="small"
        label={status}
        color={config.color}
        icon={config.icon}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          {t('reminders.admin.title')}
        </Typography>
        <Tooltip title={t('common.refresh')}>
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      {t('reminders.admin.totalSubscriptions')}
                    </Typography>
                    <Typography variant="h4">
                      {stats.total_subscriptions}
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      {t('reminders.admin.activeSubscriptions')}
                    </Typography>
                    <Typography variant="h4">
                      {stats.active_subscriptions}
                    </Typography>
                  </Box>
                  <Badge badgeContent={stats.active_subscriptions} color="success" max={999}>
                    <PeopleIcon sx={{ fontSize: 40, color: 'success.main' }} />
                  </Badge>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      {t('reminders.admin.upcomingReminders')}
                    </Typography>
                    <Typography variant="h4">
                      {stats.upcoming_reminders_7_days}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Next 7 days
                    </Typography>
                  </Box>
                  <ScheduleIcon sx={{ fontSize: 40, color: 'info.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      {t('reminders.admin.sentToday')}
                    </Typography>
                    <Typography variant="h4" color={stats.notifications_failed_today > 0 ? 'error' : 'text.primary'}>
                      {stats.notifications_sent_today}
                    </Typography>
                    {stats.notifications_failed_today > 0 && (
                      <Typography variant="caption" color="error">
                        {stats.notifications_failed_today} failed
                      </Typography>
                    )}
                  </Box>
                  <EmailIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Tabs */}
      <Card>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label={t('reminders.admin.users')}
            icon={<Badge badgeContent={subscriptions.length} color="primary"><PeopleIcon /></Badge>}
            iconPosition="start"
          />
          <Tab 
            label={t('reminders.admin.upcomingReminders')}
            icon={<Badge badgeContent={upcomingReminders.length} color="info"><ScheduleIcon /></Badge>}
            iconPosition="start"
          />
          <Tab 
            label={t('reminders.admin.viewLogs')}
            icon={<EmailIcon />}
            iconPosition="start"
          />
          <Tab 
            label={t('reminders.admin.auditLog')}
            icon={<DashboardIcon />}
            iconPosition="start"
          />
        </Tabs>

        {/* Users/Subscriptions */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('reminders.subscription.email')}</TableCell>
                  <TableCell>{t('reminders.subscription.name')}</TableCell>
                  <TableCell>{t('reminders.subscription.phone')}</TableCell>
                  <TableCell>PIN</TableCell>
                  <TableCell>{t('reminders.subscription.tier')}</TableCell>
                  <TableCell>{t('reminders.equipment.title')}</TableCell>
                  <TableCell>{t('reminders.maintenance.upcoming')}</TableCell>
                  <TableCell>{t('reminders.subscription.status')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions.map((subscription) => (
                  <TableRow key={subscription.id}>
                    <TableCell>
                      <Typography variant="body2">
                        {subscription.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{subscription.pilot_name || '-'}</TableCell>
                    <TableCell>{subscription.phone || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={subscription.pin}
                          size="small"
                          sx={{ 
                            fontFamily: 'monospace', 
                            letterSpacing: 1,
                            backgroundColor: '#f5f5f5' 
                          }}
                        />
                        <Tooltip title={copiedPin === subscription.pin ? t('common.copied') : t('common.copy')}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyPin(subscription.pin)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={subscription.service_tier}
                        color={subscription.service_tier === 'premium' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {subscription.equipment_count}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {subscription.upcoming_maintenance}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={subscription.is_active ? t('reminders.subscription.active') : t('reminders.subscription.inactive')}
                        color={subscription.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('reminders.admin.resendPin')}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleResendPin(subscription.id, subscription.email)}
                        >
                          <SendIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Upcoming Reminders */}
        <TabPanel value={tabValue} index={1}>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('reminders.subscription.email')}</TableCell>
                  <TableCell>{t('reminders.equipment.type')}</TableCell>
                  <TableCell>{t('reminders.maintenance.type')}</TableCell>
                  <TableCell>{t('reminders.maintenance.dueDate')}</TableCell>
                  <TableCell>{t('reminders.maintenance.statusLabel')}</TableCell>
                  <TableCell>Last Notification</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {upcomingReminders.map((reminder) => (
                  <TableRow key={reminder.maintenance_schedule_id}>
                    <TableCell>
                      <Typography variant="body2">
                        {reminder.pilot_email}
                      </Typography>
                      {reminder.pilot_name && (
                        <Typography variant="caption" color="text.secondary">
                          {reminder.pilot_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {reminder.equipment_type}
                      </Typography>
                      {reminder.equipment_brand && (
                        <Typography variant="caption" color="text.secondary">
                          {reminder.equipment_brand} {reminder.equipment_model}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{reminder.maintenance_type}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(reminder.due_date), 'dd/MM/yyyy')}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${reminder.days_until_due} days`}
                        color={getUrgencyColor(reminder.days_until_due)}
                        sx={{ mt: 0.5 }}
                      />
                    </TableCell>
                    <TableCell>
                      {getStatusChip(reminder.status)}
                    </TableCell>
                    <TableCell>
                      {reminder.last_notification_sent ? (
                        <Typography variant="caption">
                          {format(new Date(reminder.last_notification_sent), 'dd/MM HH:mm')}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Never
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Notification Logs */}
        <TabPanel value={tabValue} index={2}>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.date')}</TableCell>
                  <TableCell>{t('reminders.subscription.email')}</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>{t('reminders.maintenance.statusLabel')}</TableCell>
                  <TableCell>Days Before</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Typography variant="caption">
                        {format(new Date(log.sent_date), 'dd/MM/yyyy HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>{log.subscription?.email}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {log.email_subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(log.status)}
                    </TableCell>
                    <TableCell>{log.days_before_due}</TableCell>
                    <TableCell align="right">
                      {log.status === 'failed' && (
                        <Tooltip title={t('reminders.admin.resendNotification')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleResendNotification(log.id)}
                          >
                            <SendIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Audit Logs */}
        <TabPanel value={tabValue} index={3}>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.date')}</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Typography variant="caption">
                        {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>{log.admin_id}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>
                      {log.target_type && (
                        <Typography variant="caption">
                          {log.target_type}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.details && (
                        <Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>
                          {JSON.stringify(log.details)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default RemindersAdminPage;